<script setup lang="ts">
import { faArrowLeft, faFloppyDisk, faHandPointUp, faXmark } from '@fortawesome/pro-regular-svg-icons'
import LinkButtonSecondary from '@/shared/components/LinkButtonSecondary.vue'
import CardContainer from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CardHeaderItem from '@/components/Card/card-header-item.vue'
import DropdownContext from '@/components/dropdown-context.vue'
import DropdownItem from '@/components/dropdown-item.vue'
import CardContent from '@/components/Card/card-content.vue'
import InputLabel from '@/components/input-label.vue'
import TooltipIcon from '@/shared/components/TooltipIcon.vue'
import InputDate from '@/components/input-date.vue'
import { createReusableTemplate, useTimeoutPoll } from '@vueuse/core'
import ButtonSecondary from '@/components/button-secondary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import { useModalState } from '@/shared/composables/useModalState'
import AssetYieldsActivityLog from '@/features/assets/components/AssetYieldsActivityLog.vue'
import { useQuery } from '@tanstack/vue-query'
import { YieldQueries } from '@/features/assets/queries'
import ButtonPrimary from '@/components/button-primary.vue'
import {
  useYieldAction,
  useYieldFileAttachment,
  useYieldFileRemoval,
  useYieldUpdate
} from '@/features/assets/mutations'
import DrawExitStatusModal from '@/features/assets/components/DrawExitStatusModal.vue'
import { format } from 'date-fns/format'
import FileUploadButton from '@/shared/components/FileUploadButton.vue'
import { type ComponentInstance, computed, ref, readonly, watch } from 'vue'
import FileUploadList from '@/shared/components/FileUploadList.vue'
import { YieldAction, YieldFileState, YieldFileType, YieldState } from '@/features/assets/constants'
import LoaderInline from '@/shared/components/LoaderInline.vue'
import { formateDate, formatNumber } from '@/helpers/formatting'
import { getMonth } from 'date-fns/getMonth'
import BadgeRed from '@/components/badge-red.vue'
import { FieldState, TargetSystem } from '@/shared/constants'
import { buildUrl } from '@/shared/utils/urls'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import { isBefore } from 'date-fns/isBefore'
import InputNumber from '@/components/input-number.vue'
import { Log } from '@/shared/utils/logging/loggers'
import useNotifications from '@/composables/notifications'
import { getYear } from 'date-fns/getYear'
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'
import { roundToPrecision } from '@/helpers'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import IconError from '@/shared/components/IconError.vue'
import IconOk from '@/shared/components/IconOk.vue'

const props = defineProps<{ assetCode: string }>()
const lastActionUsed = ref<YieldAction>()

const { data: authenticatedUser } = useAuthenticatedUser()
const { data: yieldData, isError: isYieldError, isLoading: isYieldLoading, refetch: refetchYieldData } = useQuery(YieldQueries.fetch(() => props.assetCode))

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      designOutput: z.number().min(1).optional(),
      projectedUsage: z.number().min(1).optional(),
      expectedEndDate: z.coerce.date(),
      actualEndDate: z.date().optional(),
    })
  )
})

const { addError, addSuccess } = useNotifications()
const { pause: pauseYieldPolling, resume: resumeYieldPolling } = useTimeoutPoll(() => refetchYieldData().then(() => { }, () => { }), 5000, { immediate: false })
const { mutateAsync: attachFile } = useYieldFileAttachment(props.assetCode)
const { mutateAsync: removeFile } = useYieldFileRemoval(props.assetCode)
const { mutateAsync: addActionAsync, isPending: isAddingAction } = useYieldAction(props.assetCode)
const { open: showActivityLog, close: hideActivityLog, isOpen: isActivityLogShown } = useModalState()
const { open: showDrawExit, close: hideDrawExit, isOpen: isDrawExitShown } = useModalState()
const { mutateAsync: updateYieldAsync, isPending: isUpdatingYield } = useYieldUpdate(props.assetCode)
const hasActualData = computed(() => (yieldData.value?.monthlyEnergy ?? []).some(x => x.actual !== null))
const [DefineUploadButton, UploadButton] = createReusableTemplate<{ fileType: YieldFileType }>()
const [DefineUploadedFiles, UploadedFiles] = createReusableTemplate<{ fileType: YieldFileType }>({ inheritAttrs: false })
const [DefineActionButtons, ActionButtons] = createReusableTemplate<{ approval: YieldAction, rejection: YieldAction }>()

const submitUpdates = handleSubmit(
  async values => {
    try {
      await updateYieldAsync(values)

      addSuccess({ title: 'Yield info updated successfully.' })
    } catch (ex: unknown) {
      addError({ title: 'Unable to update yield information.' })

      Log.error('Unable to save yields', { error: ex })
    }
  }
)

// Get a constant array of all sequential months in the year.
const allMonths = readonly([...Array(12)].map((_, index) => index + 1))
const monthlyEnergy = computed(() => yieldData.value?.monthlyEnergy || [])

const disabledFileTypes = computed(
  () => {
    const currentState = yieldData.value?.state

    // If there's no state, then all file types are disabled.
    if (currentState === undefined) {
      return Object.values(YieldFileType)
    }

    // If yields are complete, then everything is disabled.
    if (currentState === YieldState.WarrantyApproved) {
      return Object.values(YieldFileType)
    }

    let disabled: YieldFileType[] = [YieldFileType.WarrantyExitDraw]

    // We're in the guarantee phase.
    if (yieldData.value?.phase === 'guarantee') {
      // If there's no actual end date, then don't allow the warranty exit draw file to be uploaded.
      disabled = !yieldData.value?.guaranteePeriodEndDateActual
        ? Object.values(YieldFileType)
        : Object.values(YieldFileType).filter(x => x !== YieldFileType.WarrantyExitDraw)
    }

    // Ensure that we include all files that have already been uploaded.
    return disabled.concat(yieldData.value?.files.map(x => x.type) ?? [])
  }
)

const deletableFileTypes = computed<YieldFileType[]>(
  () => {
    // If we're in the initial phase, then all files are deletable.
    if (yieldData.value?.phase === 'initial') {
      return Object.values(YieldFileType)
    }

    // If we're in the guarantee phase, then only the warranty yield file is deletable.
    if (yieldData.value?.phase === 'guarantee') {
      return [YieldFileType.WarrantyExitDraw]
    }

    // Otherwise, don't allow anything to be deletable. This is more than likely when there are no
    // yields or when it's complete.
    return []
  }
)

watch(
  () => yieldData.value?.hasPendingUpdate ?? false,
  hasPendingUpdate => {
    if (hasPendingUpdate) {
      resumeYieldPolling()
    } else {
      pauseYieldPolling()
    }
  }
)

function getFileAsUploadList(fileType: YieldFileType): ComponentInstance<typeof FileUploadList>['$props']['uploads'] {
  const file = yieldData.value?.files.find(x => x.type === fileType)

  if (!file) {
    return []
  }

  return [{
    id: file.uploadId,
    reason: file.stateReason ?? undefined,
    abilities: deletableFileTypes.value.includes(fileType) ? ['download', 'delete'] : ['download'],
    state: file.state == YieldFileState.Pending
      ? 'processing'
      : (
        file.state === YieldFileState.Failed
          ? 'error' :
          file.state
      )
  }]
}

function completeUpload(fileType: YieldFileType, uploadId: string, closeModal: () => void) {
  closeModal()
  attachFile({ fileType, uploadId })
}

function hasUserActioned(...actions: YieldAction[]) {
  return (
    yieldData.value?.approvals
      .received
      .some(x => actions.includes(x.action) && x.userId === authenticatedUser.value?.id)
  )
}

function addAction(action: YieldAction) {
  lastActionUsed.value = action

  addActionAsync(action).catch(
    () => {
      const isApproval = [YieldAction.GuaranteeApproval, YieldAction.ConsumptionApproval, YieldAction.WarrantyApproval].includes(action)

      addError({ title: `Unable to add ${isApproval ? 'approval' : 'rejection'}.` })
    }
  )
}

function getButtonStateForAction(action: YieldAction) {
  return (lastActionUsed.value === action && isAddingAction.value) ? 'pending' : undefined
}
</script>

<template>
  <AssetYieldsActivityLog @close="hideActivityLog" v-if="isActivityLogShown" :asset-code="assetCode" />
  <DrawExitStatusModal v-if="isDrawExitShown" @close="hideDrawExit" :asset-code="assetCode" />

  <DefineUploadButton v-slot="{ fileType }">
    <FileUploadButton
      :disabled="disabledFileTypes.includes(fileType)"
      class="w-100"
      :target-system="TargetSystem.AssetManagement"
      :max="1"
      @complete="({ uploaded, close }) => uploaded.length > 0 ? completeUpload(fileType, uploaded[0], close) : void(0)"
    />
  </DefineUploadButton>

  <DefineUploadedFiles v-slot="{ fileType }">
    <FileUploadList
      :uploads="getFileAsUploadList(fileType)"
      :on-delete="() => removeFile(fileType)"
      tooltip-placement="bottom"
    />
  </DefineUploadedFiles>

  <DefineActionButtons v-slot="{ approval, rejection }">
    <ButtonHighlighted :icon="faHandPointUp"
                       :shadow="true"
                       :state="getButtonStateForAction(approval)"
                       :disabled="isAddingAction || hasUserActioned(approval, rejection)"
                       :style="{ cursor: hasUserActioned(approval, rejection) ? 'not-allowed' : undefined, pointerEvents: 'inherit' }"
                       v-tooltip="{ text: hasUserActioned(approval, rejection) ? 'You have already approved this yield.' : undefined, placement: 'bottom' }"
                       @click="addAction(approval)">
      Approve
    </ButtonHighlighted>
    <ButtonSecondary :icon="faXmark"
                     class="ms-2"
                     :state="getButtonStateForAction(rejection)"
                     :disabled="isAddingAction || hasUserActioned(approval, rejection)"
                     :style="{ cursor: hasUserActioned(approval, rejection) ? 'not-allowed' : undefined, pointerEvents: 'inherit' }"
                     v-tooltip="{ text: hasUserActioned(approval, rejection) ? 'You have already approved this yield.' : undefined, placement: 'bottom' }"
                     @click="addAction(rejection)">
      Reject
    </ButtonSecondary>
  </DefineActionButtons>

  <LinkButtonSecondary :to="{ name: 'assets.dashboard' }" :icon="faArrowLeft">
    Back to Dashboard
  </LinkButtonSecondary>

  <CardContainer class="mt-4">
    <CardHeader size="small">
      <CardHeaderItem>Yields</CardHeaderItem>
      <CardHeaderItem>
        <DropdownContext>
          <DropdownItem @click="showActivityLog">Activity</DropdownItem>
        </DropdownContext>
      </CardHeaderItem>
    </CardHeader>

    <CardContent>
      <div :class="$style['row-top']" class="d-grid align-items-center gap-3">
          <InputLabel class="text-nowrap">
            Guarantee Expected End Date
            <TooltipIcon text="Projected end date of the guarantee period, 12 months from the asset's sale date." class="ms-1" placement="bottom" />
          </InputLabel>

          <span v-if="yieldData?.state === YieldState.WarrantyApproved">{{ formateDate(yieldData?.guaranteePeriodEndDateExpected, 'yyyy/MM/dd', '-') }}</span>
          <InputDate v-else name="expectedEndDate" inline :value="yieldData?.guaranteePeriodEndDateExpected ?? undefined" error-variant="tooltip" />

          <div class="spacer"></div>

          <InputLabel class="text-nowrap">
            Design Output
            <TooltipIcon text="Solar Yield<br>(from PV system)" class="ms-1" placement="bottom" />
          </InputLabel>
          <LoaderInline :loading="isYieldLoading" class="text-nowrap">
            <BadgeRed v-if="isYieldError">Loading error</BadgeRed>
            <template v-else>
              <InputNumber v-if="yieldData?.canUpdate" size="5" inline name="designOutput" :value="yieldData?.designOutput" error-variant="tooltip" />
              <template v-else>{{ formatNumber(yieldData?.designOutput, '-') }}</template>
              <span class="text-muted ms-1">kWh / kWp</span>
            </template>
          </LoaderInline>

          <template v-if="yieldData?.guaranteePeriodEndDateActual">
            <InputLabel class="text-nowrap">
              Guarantee Actual End Date
              <TooltipIcon text="The confirmed date when the asset exits the guarantee period and transitions to the warranty period." class="ms-1" placement="bottom" />
            </InputLabel>
            <span>{{ format(yieldData!.guaranteePeriodEndDateActual, 'yyyy/MM/dd') }}</span>
          </template>
          <div v-else style="grid-column: span 2"></div>

          <div class="spacer"></div>

          <InputLabel class="text-nowrap">
            Projected Usage
            <TooltipIcon text="Consumption Yield<br>(customer energy usage)" class="ms-1" placement="bottom" />
          </InputLabel>
          <LoaderInline :loading="isYieldLoading" class="text-nowrap">
            <BadgeRed v-if="isYieldError" class="">Loading error</BadgeRed>
            <template v-else>
              <InputNumber v-if="yieldData?.canUpdate" size="5" inline name="projectedUsage" :value="yieldData?.projectedUsage" error-variant="tooltip" />
              <template v-else>{{ formatNumber(yieldData?.projectedUsage, '-') }}</template>
              <span class="text-muted ms-1">kWh / kWp</span>
            </template>
          </LoaderInline>
      </div>

      <div :class="$style['row-yields']" class="d-grid mt-4">
        <div :class="$style.shaded">
          <!-- headers -->
          <div :class="$style.title" class="ps-0">Type</div>
          <div :class="$style.title">
            PVSyst<br>Report (CSV)
            <TooltipIcon text="Containing hourly expected energy (P90) data for 12 months." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title">
            PVSyst<br>Report (PDF)
            <TooltipIcon text="PVSyst simulation report containing the system size." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title">
            Calculation<br>Summary (PDF)
            <TooltipIcon text="A summary document of how guarantee yield is calculated." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title">
            Assumptions<br>Report (PDF)
            <TooltipIcon text="Report of assumptions used yield calculations (e.g.: downtime periods)." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title" class="text-end">Yield</div>
          <div :class="$style.title" class="text-end">
            System Size
            <TooltipIcon text="As per Sale of Business Agreement." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title" class="pe-0">Action</div>
          <!-- /headers -->

          <!-- guarantee yield #1 -->
          <div style="grid-row: span 2" class="align-self-start fw-bold ps-0">
            Guarantee<br>
            Period<br>
            Yield <TooltipIcon text="Year 1<br>typically 98% of P90." class="ms-1" placement="bottom" />
          </div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.GuaranteePvSystData" /></div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.GuaranteePvSystReport" /></div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.GuaranteeCalculationSummary" /></div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.GuaranteeAssumptionsReport" /></div>
          <div class="align-self-center text-end">
            {{ formatNumber(roundToPrecision(yieldData?.calculatedGuaranteePeriodYield?.value, 2), '-') }} <span class="text-muted">kWh / kWp</span>
            <TooltipIcon v-if="yieldData?.calculatedGuaranteePeriodYield.state" :text="yieldData?.calculatedGuaranteePeriodYield?.reason" class="ms-1" placement="bottom">
              <IconError v-if="yieldData?.calculatedGuaranteePeriodYield.state === FieldState.Error" />
              <IconOk v-if="yieldData?.calculatedGuaranteePeriodYield.state === FieldState.Ok" />
            </TooltipIcon>
          </div>
          <div class="align-self-center text-end">{{ formatNumber(roundToPrecision(yieldData?.systemSize, 2), '-') }} <span class="text-muted">kWp</span></div>
          <div class="pe-0 align-self-center text-nowrap">
              <ActionButtons v-if="yieldData?.state === YieldState.InitialApproving && yieldData?.approvals.allowedUsers.includes(authenticatedUser?.id!)"
                             :approval="YieldAction.GuaranteeApproval"
                             :rejection="YieldAction.GuaranteeRejection" />
          </div>
          <!-- /guarantee yield #1 -->

          <!-- guarantee yield #2 -->
          <div class="pt-2 align-self-start" style="grid-column-start: 2">
            <UploadedFiles :file-type="YieldFileType.GuaranteePvSystData" />
          </div>
          <div class="pt-2 align-self-start">
            <UploadedFiles :file-type="YieldFileType.GuaranteePvSystReport" />
          </div>
          <div class="pt-2 align-self-start">
            <UploadedFiles :file-type="YieldFileType.GuaranteeCalculationSummary" />
          </div>
          <div class="pt-2 align-self-start">
            <UploadedFiles :file-type="YieldFileType.GuaranteeAssumptionsReport" />
          </div>
          <div style="grid-column: 6 / -1"></div>
          <!-- /guarantee yield #2 -->

          <div style="grid-column-start: 7" class="text-end fw-bold mt-3" :class="[$style.title, $style['without-border']]">
            System Size
            <TooltipIcon text="As per Power Purchase Agreement." class="ms-1" placement="bottom" />
          </div>
          <div></div>

          <!-- consumption yield #1 -->
          <span style="grid-row: span 2" class="align-self-start fw-bold ps-0">
            Consumption<br>
            Period<br>
            Yield <TooltipIcon text="The yield guaranteed to the customer under the PPA." class="ms-1" placement="bottom" />
          </span>
          <div class="align-self-center" style="grid-column-start: 3"><UploadButton :file-type="YieldFileType.ConsumptionPvSystReport" /></div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.ConsumptionCalculationSummary" /></div>
          <div class="align-self-center"><UploadButton :file-type="YieldFileType.ConsumptionAssumptionsReport" /></div>
          <div class="align-self-center text-end">
            {{ formatNumber(roundToPrecision(yieldData?.calculatedConsumptionPeriodYield?.value, 2), '-') }} <span class="text-muted">kWh / kWp</span>
            <TooltipIcon v-if="yieldData?.calculatedConsumptionPeriodYield.state" :text="yieldData?.calculatedConsumptionPeriodYield?.reason" class="ms-1" placement="bottom">
              <IconError v-if="yieldData?.calculatedConsumptionPeriodYield.state === FieldState.Error" />
              <IconOk v-if="yieldData?.calculatedConsumptionPeriodYield.state === FieldState.Ok" />
            </TooltipIcon>
          </div>
          <div class="align-self-center text-end">{{ formatNumber(roundToPrecision(yieldData?.systemSize, 2), '-') }} <span class="text-muted">kWp</span></div>
          <div class="pe-0 align-self-center text-nowrap">
              <ActionButtons v-if="yieldData?.state === YieldState.InitialApproving && yieldData?.approvals.allowedUsers.includes(authenticatedUser?.id!)"
                             :approval="YieldAction.ConsumptionApproval"
                             :rejection="YieldAction.ConsumptionRejection" />
          </div>
          <!-- /consumption yield #1 -->

          <!-- consumption yield #2 -->
          <div class="pt-2 align-self-start" style="grid-column-start: 3">
            <UploadedFiles :file-type="YieldFileType.ConsumptionPvSystReport" />
          </div>
          <div class="pt-2 align-self-start">
            <UploadedFiles :file-type="YieldFileType.ConsumptionCalculationSummary" />
          </div>
          <div class="pt-2 align-self-start">
            <UploadedFiles :file-type="YieldFileType.ConsumptionAssumptionsReport" />
          </div>
          <div style="grid-column: 6 / -1"></div>
          <!-- /consumption yield #2 -->
        </div>

        <div :class="$style.unshaded">
          <!-- warranty yield titles -->
          <div :class="$style.title"></div>
          <div :class="$style.title" class="fw-bold">
            Exit Draw Field (PDF)
          </div>
          <div :class="$style.title" style="grid-column: 3 / 6"></div>
          <div :class="$style.title" class="text-end">Yield</div>
          <div :class="$style.title" class="text-end">
            System Size
            <TooltipIcon text="Based on the Draw Exit." class="ms-1" placement="bottom" />
          </div>
          <div :class="$style.title" class="pe-0">Action</div>
          <!-- /warranty yield titles -->

          <!-- warranty yield #1 -->
          <div style="grid-row: span 2" class="align-self-start fw-bold ps-0">
            Warranty<br>
            Period<br>
            Yield <TooltipIcon text="Based on actual production data post year 1." class="ms-1" placement="bottom" />
          </div>
          <div class="align-self-start"><UploadButton :file-type="YieldFileType.WarrantyExitDraw" /></div>
          <div style="grid-column: 3 / 6"></div>
          <div class="text-end align-self-center" :class="$style.yield">
            {{ formatNumber(yieldData?.calculatedWarrantyPeriodYield, '-') }}
            <span class="text-muted">kWh / kWp</span>
          </div>
          <div class="text-end align-self-center" :class="$style['system-size']">
            {{ formatNumber(yieldData?.systemSize, '-') }}
            <span class="text-muted">kWp</span>
          </div>
          <div class="pe-0 text-nowrap">
              <ActionButtons v-if="yieldData?.state === YieldState.GuaranteeApproving && yieldData?.approvals.allowedUsers.includes(authenticatedUser?.id!)"
                             :approval="YieldAction.WarrantyApproval"
                             :rejection="YieldAction.WarrantyRejection" />
          </div>
          <!-- /warranty yield #1 -->

          <!-- warranty yield #2 -->
          <div class="pt-2 align-self-start" style="grid-column-start: 2">
            <UploadedFiles :file-type="YieldFileType.WarrantyExitDraw" />
          </div>
          <!-- /warranty yield #2 -->
        </div>

      </div>

      <div :class="$style['monthly-yields']" v-if="monthlyEnergy.length > 0">
        <div class="text-start" :class="$style.title">Month</div>
        <div :class="$style.title">Jan</div>
        <div :class="$style.title">Feb</div>
        <div :class="$style.title">Mar</div>
        <div :class="$style.title">Apr</div>
        <div :class="$style.title">May</div>
        <div :class="$style.title">Jun</div>
        <div :class="$style.title">Jul</div>
        <div :class="$style.title">Aug</div>
        <div :class="$style.title">Sep</div>
        <div :class="$style.title">Oct</div>
        <div :class="$style.title">Nov</div>
        <div :class="$style.title">Dec</div>

        <div class="text-start fw-bold">Guarantee Period - Expected Energy (kWh)</div>
        <div v-for="month in allMonths" :key="month">
          {{ formatNumber(roundToPrecision(monthlyEnergy.find(x => x.month === month)?.expectedGuaranteePeriod, 2), '-') }}
        </div>

        <div class="text-start fw-bold">Consumption Period - Expected Energy (kWh)</div>
        <div v-for="month in allMonths" :key="month">
          {{ formatNumber(roundToPrecision(monthlyEnergy.find(x => x.month === month)?.expectedConsumptionPeriod, 2), '-') }}
        </div>

        <div class="text-start fw-bold">
          Warranty Period - Actual Energy (kWh)
          <TooltipIcon
            v-if="hasActualData && yieldData?.guaranteePeriodEndDateActual"
            class="ms-2"
            placement="bottom"
            :text="`The lightly coloured values are from the year prior to the selected actual guarantee period end date (${getYear(yieldData.guaranteePeriodEndDateActual)})`" />
        </div>
        <div v-for="month in allMonths" :class="{ 'text-muted': yieldData?.guaranteePeriodEndDateActual && month > getMonth(yieldData.guaranteePeriodEndDateActual) + 1 }" :key="month">
          {{ formatNumber(roundToPrecision(monthlyEnergy.find(x => x.month === month)?.actual, 2), '-') }}
        </div>
      </div>
    </CardContent>

  </CardContainer>

  <div class="hstack mt-4" v-if="yieldData?.state !== YieldState.WarrantyApproved">
    <ButtonPrimary :icon="faFloppyDisk" :state="isUpdatingYield ? 'pending' : undefined" @click="submitUpdates">Save</ButtonPrimary>
    <ButtonSecondary class="ms-2" :icon="faXmark" :state="isUpdatingYield ? 'pending' : undefined">Cancel</ButtonSecondary>
    <template v-if="yieldData?.phase === 'guarantee' && !yieldData?.guaranteePeriodEndDateActual && yieldData?.guaranteePeriodEndDateExpected && isBefore(yieldData!.guaranteePeriodEndDateExpected!, new Date())">
      <div class="vr ms-3 me-3"></div>
      <ButtonHighlighted shadow :icon="faHandPointUp" @click="showDrawExit">Draw Exit</ButtonHighlighted>
    </template>
  </div>
</template>

<style module lang="scss">
@import "@/assets/variables";

.row-top {
  grid-template-columns: min-content 185px 40% min-content max-content;

  .spacer {
    width: 100%;
  }
}

.title {
  align-content: end;
  font-weight: bold;
  border-bottom: 1px solid #d1d1d1;
  padding-bottom: 0.25rem;
  margin-bottom: 0.5rem;

  &.without-border {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.row-yields {
  grid-template-columns: min-content repeat(4, 1fr) repeat(2, auto) 180px;
  margin-left: #{-1 * $card-spacer-x};
  margin-right: #{-1 * $card-spacer-x};
  padding-left: #{$card-spacer-x};
  padding-right: #{$card-spacer-x};

  .row-start {
    padding-left: 0;
  }

  .row-end {
    padding-right: 0;
  }

  .unshaded > *, .shaded > * {
    padding-left: 10px;
    padding-right: 10px;
  }

  .shaded, .unshaded {
    display: grid;
    background: #f6f6f6;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    margin-left: #{-1 * $card-spacer-x};
    margin-right: #{-1 * $card-spacer-x};
    padding: #{$card-spacer-y} #{$card-spacer-y};
  }

  .unshaded {
    background: white;
  }
}

.monthly-yields {
  margin-top: 25px;
  display: grid;
  grid-template-columns: max-content repeat(12, 1fr);

  > div {
    padding: 5px;
    text-align: end;
  }

  > :global(.text-start) {
    padding-right: 40px;
  }
}
</style>
